import { ActionButton, DirectionalHint, FontIcon, FontSizes, FontWeights, IButtonStyles, Icon, IIconProps, IIconStyleProps, IIconStyles, ISearchBoxStyles, IStackItemStyles, IStyleFunctionOrObject, mergeStyles, Persona, PersonaSize, SearchBox, Stack, TooltipHost } from "@fluentui/react";
import { Text } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import UserSettingsPanel from "../src/taskpane/components/panels/UserSettingsPanel";
import { RootState } from "../src/taskpane/components/_v2/redux/storeMain";
import { IAuthAccount } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import { toggleIsContentManagerSideMenuCollapsed } from "../src/taskpane/redux/modules/contentManagerDirectory";
import { getUserProfilePicture } from "../src/taskpane/services/GraphService";
import authModule from "../utilities/AuthModule";
import { LanguageContext } from "../utilities/LocalizationModule";
import { logger } from "../utilities/logger";
import { DOCUBIRD_LOGO, getHeaderTextColor } from "../utilities/ThemeHelper";
import { useEmbeddedHeaderColor } from "../src/theme/useEmbeddedHeaderColor";
import { BRAND_HEADER } from "../src/theme/palette";
import DropDownSearch from "../src/taskpane/components/directoryComponents/DropDownSearch";
import { openHelpExplorer } from "../helpExplorer/helpExplorerNavigator";

const getHeaderStackStyles = (appColor?: string) => {
  const headerStackStyles: IStackItemStyles = {
    root: {
      minHeight: 50,
      width: "100%",
      borderBottom: "1px solid var(--db-border, #edebe9)",
      backgroundColor: appColor ? appColor : BRAND_HEADER,
      color: getHeaderTextColor(appColor)
    }
  };

  return headerStackStyles;
}

const getHeaderTextStyles = (appColor?: string) => {
  const headerTextStyles = mergeStyles({
    fontWeight: FontWeights.semibold,
    color: getHeaderTextColor(appColor),
    whiteSpace: "pre"
  });

  return headerTextStyles;
}

const headerStackItemStyles: IStackItemStyles = {
  root: {
    paddingLeft: 20,
    paddingRight: 20,
  }
};

const headerTitleStackItemStyles: IStackItemStyles = {
  root: {
    "@media only screen and (max-width: 479px)": { display: "none" },
  }
};

const headerLogoStackItemStyles: IStackItemStyles = {
  root: {
    display: 'flex',
    height: '100%',
    justifyContent: "center",
    "@media only screen and (max-width: 479px)": { display: "none" },
  }
};

const searchBoxStyles: Partial<ISearchBoxStyles> = {
  root: {
    width: "25vw",
    maxWidth: "25vw",
    borderColor: 'var(--db-accent-hover, #106ebe)',
    position: "relative",
    zIndex: 9998,
    "@media only screen and (max-width: 479px)": { width: "60vw" },
  },
  iconContainer: {
    alignItems: "center"
  }
};

const settingsButtonStyles: IButtonStyles = {
  root: {
    paddingRight: 12,
  },
  icon: {
    fontSize: FontSizes.size20,
  },
};

const getSettingsButtonIconStyles = (appColor?: string) => {
  const settingsButtonIconProps: IIconProps = {
    iconName: "Settings",
    styles: {
      root: {
        color: getHeaderTextColor(appColor)
      }
    }
  }
  return settingsButtonIconProps;
}

const getHelpButtonIconStyles = (appColor?: string) => {
  const helpButtonIconStyles: React.CSSProperties = {
    color: getHeaderTextColor(appColor),
    paddingRight: 15,
    fontSize: FontSizes.size20,
    cursor: 'pointer',
    lineHeight: "32px"
  }
  return helpButtonIconStyles;
}

const collapseMenuIconStyles: IStyleFunctionOrObject<IIconStyleProps, IIconStyles> = {
  root: {
    fontSize: 18,
    paddingTop: 7,
    paddingRight: 10,
    marginLeft: -5,
    selectors: {
      ":hover": { cursor: 'pointer' }
    }
  }
}
export interface IContentManagerHeaderProps {
  account?: IAuthAccount;
  isInEmbeddedMode?: boolean;
  isInRestrictedLocationMode?: boolean;
}

export const ContentManagerHeader = ({ account, isInEmbeddedMode, isInRestrictedLocationMode }: IContentManagerHeaderProps) => {
  const dispatch = useDispatch();

  const tenantInfo = useSelector((state: RootState) => state.tenantInfo);

  const languageStrings = React.useContext(LanguageContext);
  const [isUserSettingsPanelOpen, setIsUserSettingsPanelOpen] = React.useState<boolean>(false);
  const [profileImageUrl, setProfileImageUrl] = React.useState<string>();

  React.useEffect(() => {
    (async () => {
      try {
        const profileImageUrl = await getUserProfilePicture(await authModule.getGraphAccessToken());
        if (profileImageUrl) {
          setProfileImageUrl(profileImageUrl);
        }
      } catch (error) {
        logger(error, "ContentManagerHeader.tsx (getUserProfilePicture)");
      }
    })();
  }, []);

  const appTitle: string = React.useMemo<string>(() => {
    const title: string = tenantInfo?.appTitle || "DMS";
    return !isInEmbeddedMode || tenantInfo?.appLogo
      ? `|  ${title}`
      : title;
  }, [tenantInfo, isInEmbeddedMode]);

  const appColor: string = useEmbeddedHeaderColor(isInEmbeddedMode, tenantInfo?.appColor);

  const ProfileIcon = (profileImageUrl: string) => {
    return (
      <div
        style={{ paddingRight: 16, cursor: "pointer" }}
        onClick={() => { setIsUserSettingsPanelOpen(true) }}>
        <Persona
          imageUrl={profileImageUrl}
          text={account?.displayName}
          hidePersonaDetails
          size={PersonaSize.size32}
        />
      </div>
    )
  };

  // The DMS tour, as in the welcome splash: the Teams tab and the embedded view share this header.
  const HelpButton = () => {
    return (
      <TooltipHost content="Explore" directionalHint={DirectionalHint.leftTopEdge}>
        <FontIcon iconName="Help" onClick={() => openHelpExplorer(false, "dms")} style={getHelpButtonIconStyles(appColor)} />
      </TooltipHost>
    )
  };

  const UserSettingsButton = () => {
    return (
      <ActionButton iconProps={getSettingsButtonIconStyles(appColor)} onClick={() => setIsUserSettingsPanelOpen(true)} styles={settingsButtonStyles} />
    )
  };

  return (
    <>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between" styles={getHeaderStackStyles(appColor)}>
        <Stack.Item styles={headerStackItemStyles} className="header-container" style={isInRestrictedLocationMode ? { paddingLeft: 0 } : undefined}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} style={{ height: "100%" }}>
            {!isInRestrictedLocationMode && <Stack.Item>
              <Icon iconName="CollapseMenu" styles={collapseMenuIconStyles} onClick={() => dispatch(toggleIsContentManagerSideMenuCollapsed())} />
            </Stack.Item>}
            {(!isInEmbeddedMode || tenantInfo.appLogo) && <Stack.Item align="center" styles={headerLogoStackItemStyles}>
              <img src={tenantInfo.appLogo || DOCUBIRD_LOGO} className="header-logo" />
            </Stack.Item>}
            <Stack.Item styles={headerTitleStackItemStyles}>
              <Text variant="large" className={getHeaderTextStyles(appColor)} style={isInRestrictedLocationMode && !tenantInfo?.appLogo ? { marginLeft: 8 } : undefined} >{appTitle}</Text>
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item styles={headerStackItemStyles}>
          <DropDownSearch searchBoxStyles={searchBoxStyles} />
        </Stack.Item>
        <Stack.Item>
          <Stack horizontal verticalAlign="center">
            {HelpButton()}
            {
              account && (!isInEmbeddedMode ? ProfileIcon(profileImageUrl) : UserSettingsButton())
            }
          </Stack>
        </Stack.Item>
      </Stack>
      {isUserSettingsPanelOpen && <UserSettingsPanel isInRestrictedLocationMode={isInRestrictedLocationMode} profileImageUrl={profileImageUrl} isOpen={isUserSettingsPanelOpen} closePanel={() => setIsUserSettingsPanelOpen(false)} />}
    </>
  )
};